import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { DATETIME_FORMAT } from '../constants';
import SortUtil from '../util/SortUtil';
import UiTable from '../uiTable';
import UiButton from '../uiButton';
import UiPagination from '../uiPagination';
import './index.css';
import { styled } from './styles';

const ASC = 'ASC';
const DESC = 'DESC';
export const COLUMN_TYPE_TIMESTAMP = 'TIMESTAMP';
export const COLUMN_TYPE_PICTURE = 'PICTURE';

/**
 * Table sort
 * Show a table with sort icons in the title and pagination
 * Props:
 * @param number rowsPage: quantity of rows show per page
 * @param array data
 * @param object columnsFormat = {nameColumn: {
 *  bool allowSort
 *  string title
 *  string type [COLUMN_TYPE_TIMESTAMP, COLUMN_TYPE_PICTURE]
 * }}
 * @param string mode
 *
 * Note: the columnsFormat.nameColumn must be the same name used in the column
 * names of the array data
 * COLUMN_TYPE_TIMESTAMP: columns with this type will be shown in format DATETIME
 */
export class TableSort extends Component {
  constructor(props) {
    super(props);
    this.tableData = this.tableData.bind(this);
    this.rowTitles = this.rowTitles.bind(this);
    this.dataValue = this.dataValue.bind(this);
    this.pagination = this.pagination.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.sortBy = this.sortBy.bind(this);
    this.updateTitle = this.updateTitle.bind(this);
    const { columnsFormat } = this.props;
    let titles = [];
    if (Object.keys(columnsFormat).length > 0) {
      titles = Object.keys(columnsFormat).map((key) => ({
        name: key,
        title: columnsFormat[key].title,
        allowSort: columnsFormat[key].allowSort,
        sort: columnsFormat[key].allowSort ? false : null,
        direction: columnsFormat[key].allowSort ? DESC : null,
        type: columnsFormat[key].type ? columnsFormat[key].type : null,
      }));
    }
    this.state = {
      titles,
      activePage: 1,
    };
  }

  /**
   * componentDidUpdate
   * If the titles (columnsFormat) are changed, then we need to update it
   * without to change the state sort
   */
  componentDidUpdate(prevProps) {
    let { columnsFormat } = this.props;
    columnsFormat = Object.assign({}, columnsFormat);

    let columnsFormatPrevProp = prevProps.columnsFormat;
    columnsFormatPrevProp = Object.assign({}, columnsFormatPrevProp);
    if (
      Object.keys(columnsFormat).length > 0 &&
      Object.keys(columnsFormatPrevProp).length > 0
    ) {
      let update = false;
      Object.keys(columnsFormat).forEach((key) => {
        if (
          columnsFormatPrevProp[key] === undefined ||
          columnsFormat[key].title !== columnsFormatPrevProp[key].title
        ) {
          update = true;
        }
      });
      if (update) {
        this.updateTitle(columnsFormat);
      }
    }
  }

  /**
   * On page change
   * This event is called by onCLick on the pagination component
   * @param object event
   * @param object data
   */
  onPageChange(event, data) {
    const { activePage } = data;
    this.setState({
      activePage,
    });
    window.scrollTo(0, 0);
  }

  /**
   * Update title
   * If the titles were changed, we need to update it without to change the sort state
   * @param object columnsFormat
   */
  updateTitle(columnsFormat) {
    const { titles } = this.state;
    if (Object.keys(columnsFormat).length > 0) {
      const titlesNew = [];
      Object.keys(columnsFormat).forEach((key) => {
        const title = titles.find((t) => t.name === key);
        const titleNew = {
          name: key,
          title: columnsFormat[key].title,
          allowSort: columnsFormat[key].allowSort,
          sort: title ? title.sort : null,
          direction: title ? title.direction : null,
          type: columnsFormat[key].type ? columnsFormat[key].type : null,
        };
        titlesNew.push(titleNew);
      });
      this.setState({
        titles: titlesNew,
      });
    }
  }

  /**
   * Row titles
   * Create the row titles including a sort button
   * @return object
   */
  rowTitles() {
    const { titles } = this.state;
    const { mode, classes } = this.props;
    const header = titles.map((title, index) => ({
      key: index.toString(),
      content: (
        <div className={classes.header}>
          {title.title}
          {title.allowSort && (
            <UiButton
              id={index.toString()}
              basic
              iconName={title.direction === DESC ? 'caret up' : 'caret down'}
              onClick={() =>
                this.sortBy(title.name, title.direction === DESC ? ASC : DESC)
              }
              showLabels={false}
              mode={mode}
            />
          )}
        </div>
      ),
    }));
    return header;
  }

  /**
   * Data value
   * Returns the value follow the settings of the object columnsFormat
   * @param string value
   * @param string name
   * @return string|object
   */
  dataValue(value, name) {
    const { columnsFormat } = this.props;
    switch (columnsFormat[name].type) {
      case COLUMN_TYPE_TIMESTAMP:
        return Moment(value * 1000).format(DATETIME_FORMAT);

      default:
        return value;
    }
  }

  /**
   * Table data
   * @param array data
   * @return object
   */
  tableData(data) {
    const rows = data.map((row, index) => ({
      key: index.toString(),
      columns: Object.keys(row).map((key, indexRow) => ({
        key: indexRow.toString(),
        content: this.dataValue(row[key], key),
      })),
    }));
    return rows;
  }

  /**
   * Sort by
   * @param string field
   */
  sortBy(field) {
    const { titles } = this.state;
    const titleToSort = titles.find((value) => value.name === field);
    titleToSort.direction = titleToSort.direction === ASC ? DESC : ASC;
    titles.forEach((title) => {
      Object.assign(title, { sort: title.name === field });
    });
    this.setState({
      titles,
    });
  }

  /**
   * Pagination
   * Creates pagination component
   * @return object
   */
  pagination() {
    const { data, rowsPage, mode } = this.props;
    return (
      <UiPagination
        defaultActivePage={1}
        totalPages={Math.ceil(data.length / rowsPage)}
        onPageChange={this.onPageChange}
        mode={mode}
      />
    );
  }

  /**
   * Data to shown
   * Select the data to be shown acording the pagination
   * @param array data
   * @return array dataPage (slice)
   */
  dataToShow(data) {
    const { rowsPage } = this.props;
    const { activePage } = this.state;
    /* Check if the active page has data to be shown: reason
    change the quantity of data to be show from outside */
    if ((activePage - 1) * rowsPage > data.length) {
      return data.slice(0, rowsPage);
    }
    const dataPage = data.slice(
      (activePage - 1) * rowsPage,
      (activePage - 1) * rowsPage + rowsPage
    );

    return dataPage;
  }

  render() {
    const { data, mode } = this.props;
    let dataView = data.length > 0 ? data.slice() : [];
    const { titles } = this.state;
    /* find current sort value: if title has the value sort = true then
     the table should be sorted by this field  */
    const title = titles.find((value) => value.sort);
    if (title !== undefined) {
      dataView = SortUtil.multisort(
        data,
        [title.name],
        [title.direction === ASC ? DESC : ASC]
      );
    }
    const dataPage = this.dataToShow(dataView);
    return (
      <div className="table-sort">
        <UiTable
          header={this.rowTitles()}
          content={this.tableData(dataPage)}
          mode={mode}
        />
        {data.length > 0 && this.pagination()}
      </div>
    );
  }
}

TableSort.propTypes = {
  rowsPage: PropTypes.number,
  data: PropTypes.arrayOf(Object).isRequired,
  columnsFormat: PropTypes.shape({
    allowSort: PropTypes.bool,
    title: PropTypes.objectOf(Object),
    type: PropTypes.string,
  }).isRequired,
  mode: PropTypes.string,
};

TableSort.defaultProps = {
  rowsPage: 20,
  mode: null,
};

export default styled(TableSort);
